import React, { useEffect, useCallback, useMemo } from 'react';
import { ChatProvider, useChatContext } from '../bot-core/store/ChatContext';
import { BotExtProvider, useBotContext } from './store/BotContext';
import { useBotSession } from './hooks/useBotSession';
import { useChatSocket } from '../bot-core/hooks/useChatSocket';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import ChatButton from '../bot-core/ChatButton';
import ChatPanel from '../bot-core/ChatPanel';
import ChatHeader from '../bot-core/ChatHeader';
import ChatMessages from '../bot-core/ChatMessages';
import ChatInput from '../bot-core/ChatInput';
import ItinerarySummaryCard from './ItinerarySummaryCard';
import CheckoutPanel from './CheckoutPanel';
import CheckoutCtaButton from './CheckoutCtaButton';
import BotMessageExtras from './BotMessageExtras';
import { UIMessage } from '../bot-core/types/chat.types';
import { IoBagHandleOutline } from 'react-icons/io5';
import { Tabs } from 'antd';
import { useBotCheckoutHandler, extractBotSessionId } from './hooks/useBotCheckoutHandler';
import { handleBotTurnComplete } from './hooks/handleBotTurnComplete';
import { useWhatsappHandoff } from './hooks/useWhatsappHandoff';
import type {
  WhatsappAvailability,
  BotProductType,
} from './hooks/useBotAgentAvailability';
import { buildEmptyStateSubtitle } from './utils/buildEmptyStateSubtitle';
import { FaWhatsapp } from 'react-icons/fa';
import '../bot-core/chat.less';
import './bot.less';

interface BotWidgetInnerProps {
  whatsapp?: WhatsappAvailability | null;
  enabledProducts?: BotProductType[] | null;
}

const BotWidgetInner: React.FC<BotWidgetInnerProps> = ({
  whatsapp,
  enabledProducts,
}) => {
  const { state: chatState, dispatch: chatDispatch } = useChatContext();
  const { botState, botDispatch } = useBotContext();
  const {
    initSession,
    ensureSession,
    startNewChat,
    refreshSelections,
    fetchCheckout,
    tabs,
    activeTabId,
    switchTab,
    closeTab,
  } = useBotSession();

  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const tenantId = Number(process.env.REACT_APP_TENANT_ID) || undefined;

  const onDone = useCallback(async (meta: any) => {
    if (!botState.sessionId) return;
    await handleBotTurnComplete(
      botState.sessionId,
      meta,
      refreshSelections,
      fetchCheckout,
      () => !!botState.checkoutPayload || !!botState.checkoutExpiresAt,
    );
  }, [
    botState.sessionId,
    botState.checkoutPayload,
    botState.checkoutExpiresAt,
    refreshSelections,
    fetchCheckout,
  ]);

  const onHandedOff = useCallback(
    (handedSessionId: string) => {
      if (handedSessionId === botState.sessionId) {
        chatDispatch({ type: 'SET_OPEN', isOpen: false });
        chatDispatch({
          type: 'ADD_MESSAGE',
          message: {
            id: crypto.randomUUID(),
            role: 'system',
            // TODO(i18n): translation key bot.handoff.handedOff
            text: 'This conversation continues on WhatsApp.',
            timestamp: new Date().toISOString(),
          } as UIMessage,
        });
      }
    },
    [botState.sessionId, chatDispatch],
  );

  const { connect, disconnect, emit } = useChatSocket({
    namespace: '/bot-agent',
    onDone,
    onHandedOff,
  });

  const { handoff: triggerWhatsappHandoff, busy: handoffBusy } =
    useWhatsappHandoff();

  useEffect(() => { initSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Connect on open regardless of whether a session exists yet — the gateway
  // only authenticates on connect, it doesn't need a session. Connecting early
  // means the socket is ready by the time the first message is sent (which is
  // also what lazily creates the session), so the send_message emit isn't
  // racing the WebSocket handshake.
  useEffect(() => {
    if (chatState.isOpen) connect();
    else disconnect();
  }, [chatState.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = () => chatDispatch({ type: 'SET_OPEN', isOpen: !chatState.isOpen });
  const handleClose = () => chatDispatch({ type: 'SET_OPEN', isOpen: false });

  const handleSend = (
    text: string,
    opts?: {
      selection?: {
        book_hash?: string;
        package_room?: {
          room_id?: string | number;
          original_room_id?: string;
          room_selection_key?: string;
          adult?: number;
          child?: number;
          infant?: number;
        };
        event_ticket?: {
          ticket_id?: string | number;
          quantity?: number;
          delivery_method_id?: number;
        };
      };
    },
  ) => {
    // Lazily create the session on the first message so opening the widget
    // never persists an empty session. ensureSession returns the existing id
    // when one already exists, so subsequent sends just emit.
    void (async () => {
      const sessionId = botState.sessionId ?? (await ensureSession());
      if (!sessionId) return;
      emit('send_message', {
        session_id: sessionId,
        text,
        ...(tenantId ? { tenant_id: tenantId } : {}),
        // Structured room selection rides alongside the friendly text so the BE
        // can hand the LLM the exact book_hash (see BotAgentService selection hint).
        ...(opts?.selection ? { selection: opts.selection } : {}),
      });
    })();
  };

  const isBlocked = botState.sessionStatus === 'blocked';
  const hasActivity = chatState.messages.length > 0 || botState.selections.length > 0;

  const handleRemoveItem = (displayName: string) => {
    handleSend(`Remove ${displayName}`);
  };

  // Intercepts the bot-generated checkout link (`/shopping-cart?bot_session_id=…`).
  // Builds the V2 cart from BE, stashes to localStorage in the shape the
  // existing Checkout page expects, then navigates to `/checkout/<uuid>`.
  // Non-bot links fall through to the default react-router / `<a>` behaviour.
  const handleBotCheckout = useBotCheckoutHandler();
  const onLinkClick = useCallback(
    (url: string, _e: React.MouseEvent): boolean => {
      const sid = extractBotSessionId(url);
      if (!sid) return false;
      handleBotCheckout(sid);
      return true;
    },
    [handleBotCheckout],
  );

  const renderMessageExtra = (msg: UIMessage) => (
    <BotMessageExtras msg={msg} onSend={handleSend} />
  );

  const showCheckoutCta =
    !!botState.checkoutPayload &&
    !!botState.checkoutExpiresAt &&
    !chatState.isStreaming;
  const checkoutCtaBar = showCheckoutCta ? (
    <div className="bot-checkout-bar">
      <CheckoutCtaButton
        sessionId={botState.checkoutPayload!.session_id}
        expiresAt={botState.checkoutExpiresAt ?? undefined}
        onCheckout={handleBotCheckout}
        label={t?.checkout?.completeBooking}
      />
    </div>
  ) : null;

  const handleToggleCheckoutPanel = useCallback(async () => {
    if (botState.checkoutReady) {
      botDispatch({ type: 'HIDE_CHECKOUT' });
      return;
    }
    if (botState.sessionId && botState.checkoutPayload) {
      await fetchCheckout(botState.sessionId);
    }
    botDispatch({ type: 'SHOW_CHECKOUT' });
  }, [
    botState.checkoutReady,
    botState.sessionId,
    botState.checkoutPayload,
    botDispatch,
    fetchCheckout,
  ]);

  // "Preview cart" chip lives in the header for the ACTIVE tab — single
  // chip across the strip per the agreed UX. Toggles panel via SHOW/HIDE.
  // Always visible while there's a payload, regardless of panel state, so
  // it remains a discoverable indicator that the active tab has a checkout.
  const hasCheckoutPayload = !!botState.checkoutPayload;
  const showWhatsappHandoff =
    !!whatsapp?.enabled &&
    !!whatsapp?.business_number &&
    !!botState.sessionId;

  const headerActions = (
    <>
      {showWhatsappHandoff && (
        <button
          className="bot-icon-btn"
          // TODO(i18n): translation key bot.handoff.continueOnWhatsapp
          title="Continue on WhatsApp"
          disabled={handoffBusy}
          onClick={() => {
            if (botState.sessionId) triggerWhatsappHandoff(botState.sessionId);
          }}
        >
          <FaWhatsapp size={20} />
        </button>
      )}
      {hasCheckoutPayload && (
        <button
          className={`bot-icon-btn${botState.checkoutReady ? ' bot-icon-btn--active' : ''}`}
          title={t?.previewCart ?? 'Preview cart'}
          onClick={handleToggleCheckoutPanel}
        >
          <IoBagHandleOutline size={20} />
        </button>
      )}
    </>
  );

  // Whenever the active session shows its first user message, cache it as
  // the label so the tab strip keeps it after switching away (Redux-backed
  // `tabTitles` map — see BotContext.SET_TAB_TITLE).
  const firstUserText = useMemo(() => {
    const m = chatState.messages.find((m) => m.role === 'user');
    return m?.text;
  }, [chatState.messages]);
  useEffect(() => {
    if (activeTabId && firstUserText) {
      botDispatch({
        type: 'SET_TAB_TITLE',
        sessionId: activeTabId,
        title: firstUserText.slice(0, 40),
      });
    }
  }, [activeTabId, firstUserText, botDispatch]);

  // Browser-style tabs in the chat header. Label resolution order:
  //   1. botState.tabTitles[id] (persistent cache, survives switches)
  //   2. tab.title (snapshot at create time)
  //   3. localized "New chat" placeholder
  const newChatLabel = t?.newChat ?? 'New chat';
  const tabItems = tabs.map((tab) => ({
    key: tab.id,
    label:
      (botState.tabTitles[tab.id] && botState.tabTitles[tab.id].trim()) ||
      (tab.title && tab.title.trim()) ||
      newChatLabel,
    // The chat panel itself is rendered ONCE outside the Tabs container —
    // tab switching reloads the active session into that single panel.
    // Using `children` here just satisfies AntD's API.
    children: null,
    closable: true,
  }));

  const tabsStrip = tabs.length > 0 ? (
    <div className="bot-tabs-strip">
      <Tabs
        type="editable-card"
        activeKey={activeTabId ?? undefined}
        items={tabItems}
        hideAdd={false}
        onChange={(key) => switchTab(key)}
        onEdit={(targetKey, action) => {
          if (action === 'add') {
            startNewChat();
          } else if (action === 'remove' && typeof targetKey === 'string') {
            closeTab(targetKey);
          }
        }}
      />
    </div>
  ) : null;

  return (
    <>
      <ChatButton isOpen={chatState.isOpen} onClick={handleToggle} hasActivity={hasActivity} />
      {chatState.isOpen && (
        <ChatPanel
          header={
            <>
              <ChatHeader
                title={t?.title ?? 'Sky360 Travel Assistant'}
                avatarEmoji="✈️"
                extraActions={headerActions}
                onClose={handleClose}
              />
              {tabsStrip}
            </>
          }
          body={
            <ChatMessages
              avatarEmoji="✈️"
              isBlocked={isBlocked}
              emptyStateContent={
                <div className="bot-empty-state">
                  <div className="bot-empty-state__icon">✈️</div>
                  <div className="bot-empty-state__title">{t?.emptyState?.title ?? 'How can I help plan your trip?'}</div>
                  <div className="bot-empty-state__sub">{buildEmptyStateSubtitle(t?.emptyState, enabledProducts)}</div>
                </div>
              }
              blockedMessage={t?.blocked ?? 'This chat has been disabled.'}
              // extraBottom={botState.selections.length > 0 ? <ItinerarySummaryCard onRemove={handleRemoveItem} /> : null}
              renderMessageExtra={renderMessageExtra}
              onLinkClick={onLinkClick}
            />
          }
          footer={
            <>
              {checkoutCtaBar}
              <ChatInput
                onSend={handleSend}
                isBlocked={isBlocked}
                placeholder={t?.input?.placeholder ?? 'Ask me anything about your trip\u2026'}
              />
            </>
          }
          overlay={botState.checkoutReady ? <CheckoutPanel /> : undefined}
        />
      )}
    </>
  );
};

interface BotWidgetProps {
  whatsapp?: WhatsappAvailability | null;
  enabledProducts?: BotProductType[] | null;
}

const BotWidget: React.FC<BotWidgetProps> = ({ whatsapp, enabledProducts }) => (
  <ChatProvider>
    <BotExtProvider>
      <BotWidgetInner whatsapp={whatsapp} enabledProducts={enabledProducts} />
    </BotExtProvider>
  </ChatProvider>
);

export default BotWidget;
